import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { IdentityUser } from "../lib/identity";
import type { AuthedUser } from "../types/authedUser";
import type { Role } from "@shared/types";

/**
 * Avatar-triggered account menu (Outlook-style): shows who's logged in and
 * where to go from here, rather than being a single-purpose control. Keep
 * this as the entry point for anything account/app-preference related —
 * add new destinations as menu items, not by growing this popover into a
 * second settings surface.
 */
export function UserMenu({
  user,
  appUser,
  role,
  onLogout,
}: {
  user: IdentityUser;
  appUser: AuthedUser | null;
  role: Role | null;
  onLogout: () => void;
}) {
  const displayName = appUser?.fullName || user.email;
  const avatarUrl = appUser?.avatarUrl ?? null;
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  function goToSettings() {
    setOpen(false);
    navigate("/settings");
  }

  return (
    <div className="user-menu-wrap" ref={wrapRef}>
      <button
        type="button"
        className="avatar avatar-btn"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Account"
        aria-label="Account menu"
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt="" className="avatar-img" />
        ) : (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="5.5" r="2.75" stroke="currentColor" strokeWidth="1.3" />
            <path d="M2.75 13.25c0-2.485 2.351-4.5 5.25-4.5s5.25 2.015 5.25 4.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
          </svg>
        )}
      </button>
      {open && (
        <div className="user-menu" role="menu" aria-label="Account">
          <div className="user-menu-header">
            <div className="avatar user-menu-avatar">
              {avatarUrl ? (
                <img src={avatarUrl} alt="" className="avatar-img" />
              ) : (
                <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="5.5" r="2.75" stroke="currentColor" strokeWidth="1.3" />
                  <path d="M2.75 13.25c0-2.485 2.351-4.5 5.25-4.5s5.25 2.015 5.25 4.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                </svg>
              )}
            </div>
            <div className="user-menu-header-text">
              <span className="user-menu-email">{displayName}</span>
              {role && <span className="user-menu-role">{role.replace(/_/g, " ")}</span>}
            </div>
          </div>
          <div className="user-menu-divider" />
          <button type="button" role="menuitem" className="user-menu-item" onClick={goToSettings}>
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.3" />
              <path
                d="M8 2.2v1.5M8 12.3v1.5M13.8 8h-1.5M3.7 8H2.2M11.9 4.1l-1 1M5.1 10.9l-1 1M11.9 11.9l-1-1M5.1 5.1l-1-1"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="round"
              />
            </svg>
            <span>Settings</span>
          </button>
          <div className="user-menu-divider" />
          <button type="button" role="menuitem" className="user-menu-item" onClick={onLogout}>
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
              <path d="M6.5 14H3.5A1.5 1.5 0 0 1 2 12.5v-9A1.5 1.5 0 0 1 3.5 2h3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
              <path d="M10.5 11 14 8l-3.5-3M14 8H6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Log out</span>
          </button>
        </div>
      )}
    </div>
  );
}
